/**
 * @file hooks/useSpeechToText.ts
 * 7.3-07 — Dictée vocale via la Web Speech API (SpeechRecognition).
 *
 * 100% front, aucun backend requis. Support navigateur variable :
 * excellent sur Chrome/Edge, partiel sur Safari, absent sur Firefox —
 * d'où `supported` pour une dégradation gracieuse (bouton masqué).
 *
 * La transcription est exposée en deux morceaux :
 *  - `transcript`  : segments finalisés (cumulés depuis start())
 *  - `interim`     : hypothèse en cours (résultats intermédiaires)
 * L'appelant compose le champ texte ; l'utilisateur valide avant envoi.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// ── Typage minimal de la Web Speech API (absente de lib.dom) ─────────────────

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: SpeechRecognitionAlternativeLike;
  length: number;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}

interface SpeechRecognitionErrorEventLike {
  error?: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Messages d'erreur lisibles (français) pour les codes de l'API. */
function humanError(code: string | undefined): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Accès au micro refusé. Autorisez le micro dans les réglages du navigateur.';
    case 'no-speech':
      return 'Aucune parole détectée.';
    case 'audio-capture':
      return 'Aucun micro détecté.';
    case 'network':
      return 'La reconnaissance vocale a échoué (réseau).';
    default:
      return 'La dictée vocale a rencontré une erreur.';
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface SpeechToText {
  /** L'API est-elle disponible sur ce navigateur ? */
  supported: boolean;
  /** Écoute en cours ? */
  listening: boolean;
  /** Segments finalisés depuis le dernier start(). */
  transcript: string;
  /** Hypothèse intermédiaire en cours (non finalisée). */
  interim: string;
  /** Erreur lisible, ou null. */
  error: string | null;
  start: () => void;
  stop: () => void;
  /** Réinitialise transcript/interim/error. */
  reset: () => void;
}

export function useSpeechToText(lang = 'fr-FR'): SpeechToText {
  const ctorRef = useRef<SpeechRecognitionCtor | null>(getSpeechRecognitionCtor());
  const supported = ctorRef.current !== null;

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const finalRef = useRef('');
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Arrêt propre au démontage (sinon le micro reste ouvert).
  useEffect(
    () => () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    },
    [],
  );

  const reset = useCallback(() => {
    finalRef.current = '';
    setTranscript('');
    setInterim('');
    setError(null);
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    // setListening(false) arrive via onend (arrêt manuel ou auto sur silence).
  }, []);

  const start = useCallback(() => {
    const Ctor = ctorRef.current;
    if (!Ctor || recognitionRef.current) return;

    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (e: SpeechRecognitionEventLike) => {
      let interimText = '';
      const results = e.results;
      const total = results.length;
      // Les résultats avant resultIndex sont déjà traités ; on parcourt la
      // fenêtre [resultIndex, length) sans boucle imbriquée inutile.
      Array.from({ length: total - e.resultIndex }, (_, i) => e.resultIndex + i).forEach(
        (idx) => {
          const result = results[idx];
          if (!result) return;
          const text = result[0]?.transcript ?? '';
          if (result.isFinal) {
            finalRef.current += text;
          } else {
            interimText += text;
          }
        },
      );
      setTranscript(finalRef.current);
      setInterim(interimText);
    };

    rec.onerror = (e: SpeechRecognitionErrorEventLike) => {
      // 'no-speech' est bénin (silence) : pas d'erreur bloquante affichée.
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        setError(humanError(e.error));
      }
    };

    rec.onend = () => {
      // Arrêt manuel OU auto (silence prolongé, perte réseau…).
      recognitionRef.current = null;
      setListening(false);
      setInterim('');
    };

    setError(null);
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      recognitionRef.current = null;
      setListening(false);
      setError(humanError(undefined));
    }
  }, [lang]);

  return { supported, listening, transcript, interim, error, start, stop, reset };
}
